import localforage from "localforage"
import noop from "lib/noop"

const DRAFT_EXPIRY_MS = 24 * 60 * 60 * 1000 // 24 hours

const store = localforage.createInstance({ name: "sfg20-local-drafts" })

/**
 * Get a user-scoped key to isolate drafts per authenticated user.
 * Prevents cross-account edits if sessions change without a full logout.
 */
function scopedKey(key) {
    const email = window._authorization?.user?.attributes?.email ?? window._authorization?.user?.email ?? "anonymous"
    return `${email}:${key}`
}

/**
 * Save a local draft to IndexedDB.
 * @param {string} key - Draft key (e.g. "custom:{draftId}" or "tailoring:{treeId}:{scheduleInGroupId}")
 * @param {object} data - The draft payload data
 * @param {*} serverVersion - The server version when editing began
 * @param {string} editorType - "custom-schedule" or "regime-tailoring"
 * @param {object} [saveContext] - Context needed to reconstruct the save call without the editor mounted
 */
export async function saveLocalDraft(key, data, serverVersion, editorType, saveContext) {
    // JSON round-trip strips non-serializable values (functions, prototypes)
    // that the Bound/useUndo system may attach to objects. Without this,
    // IndexedDB's structured clone algorithm throws on such properties.
    await store.setItem(scopedKey(key), {
        key,
        data: jsonClone(data),
        serverVersion,
        updatedAt: Date.now(),
        editorType,
        saveContext: saveContext ? jsonClone(saveContext) : undefined,
        originInstance: saveContext?.writeOrderInstance || null,
        conflictDetected: false,
    })
}

export function jsonClone(obj) {
    return JSON.parse(JSON.stringify(obj))
}

/**
 * Get a local draft by key.
 * @param {string} key
 * @returns {Promise<{key: string, data: object, serverVersion: *, updatedAt: number, editorType: string, conflictDetected: boolean}|null>}
 */
export async function getLocalDraft(key) {
    try {
        const draft = await store.getItem(scopedKey(key))
        if (!draft) return null
        if (Date.now() - draft.updatedAt > DRAFT_EXPIRY_MS) {
            await store.removeItem(scopedKey(key))
            return null
        }
        return draft
    } catch (e) {
        console.warn("Failed to get local draft:", e)
        return null
    }
}

/**
 * Clear a local draft by key.
 * @param {string} key
 */
export async function clearLocalDraft(key) {
    try {
        await store.removeItem(scopedKey(key))
    } catch (e) {
        console.warn("Failed to clear local draft:", e)
    }
}

/**
 * Clear a local draft only if it hasn't been updated since the given timestamp.
 * Prevents an older in-flight save from deleting a newer queued draft.
 * @param {string} key
 * @param {number} savedAt - Timestamp when the save that succeeded was initiated
 */
export async function clearLocalDraftIfNotNewer(key, savedAt) {
    try {
        const sk = scopedKey(key)
        const draft = await store.getItem(sk)
        if (!draft) return
        if (draft.updatedAt <= savedAt) {
            await store.removeItem(sk)
        }
    } catch (e) {
        console.warn("Failed to clear local draft:", e)
    }
}

/**
 * Update local draft metadata without changing the queued payload.
 * Used by background sync recovery to refresh version/context after the
 * server accepted an earlier write or reports a newer server version.
 * @param {string} key
 * @param {object} updates
 */
export async function updateLocalDraftMetadata(key, updates = {}) {
    try {
        const sk = scopedKey(key)
        const draft = await store.getItem(sk)
        if (!draft) return null
        const next = {
            ...draft,
            ...updates,
            saveContext: updates.saveContext ? jsonClone(updates.saveContext) : draft.saveContext,
        }
        await store.setItem(sk, next)
        return next
    } catch (e) {
        console.warn("Failed to update local draft metadata:", e)
        return null
    }
}

/**
 * Get all pending (non-expired) drafts.
 * @returns {Promise<Array>}
 */
export async function getAllPendingDrafts() {
    try {
        const prefix = currentUserPrefix()
        const drafts = []
        await store.iterate((value, key) => {
            if (!key.startsWith(prefix)) return undefined
            if (Date.now() - value.updatedAt > DRAFT_EXPIRY_MS) {
                store.removeItem(key).catch(noop)
            } else if (!value.conflictDetected) {
                drafts.push(value)
            }
            return undefined
        })
        return drafts
    } catch (e) {
        console.warn("Failed to get pending drafts:", e)
        return []
    }
}

/**
 * Clear all local drafts for the current user.
 * Scoped by user prefix so other users' drafts on shared browsers are preserved.
 */
export async function clearAllDrafts() {
    try {
        const prefix = currentUserPrefix()
        const keysToRemove = []
        await store.iterate((value, key) => {
            if (key.startsWith(prefix)) keysToRemove.push(key)
            return undefined
        })
        for (const key of keysToRemove) {
            await store.removeItem(key)
        }
    } catch (e) {
        console.warn("Failed to clear all drafts:", e)
    }
}

/**
 * Check if there are any pending local drafts.
 * @returns {Promise<boolean>}
 */
export async function hasAnyPendingDrafts() {
    try {
        const prefix = currentUserPrefix()
        let found = false
        await store.iterate((value, key) => {
            if (!key.startsWith(prefix)) return undefined
            if (Date.now() - value.updatedAt <= DRAFT_EXPIRY_MS) {
                found = true
                return found // stops iteration by returning non-undefined
            }
            return undefined
        })
        return found
    } catch (e) {
        console.warn("Failed to check pending drafts:", e)
        return false
    }
}

/**
 * Return the scoped key prefix for the current user.
 */
export function currentUserPrefix() {
    const email = window._authorization?.user?.attributes?.email ?? window._authorization?.user?.email ?? "anonymous"
    return `${email}:`
}

/**
 * Mark a draft as having a conflict detected.
 * @param {string} key
 */
export async function markConflict(key) {
    try {
        const sk = scopedKey(key)
        const draft = await store.getItem(sk)
        if (draft) {
            draft.conflictDetected = true
            await store.setItem(sk, draft)
        }
    } catch (e) {
        console.warn("Failed to mark conflict:", e)
    }
}

/**
 * Save an emergency draft to localStorage (synchronous, for beforeunload).
 * @param {string} key
 * @param {object} data
 * @param {*} serverVersion
 * @param {string} editorType
 * @param {object} [saveContext]
 */
export function saveEmergencyDraft(key, data, serverVersion, editorType, saveContext) {
    try {
        localStorage.setItem(
            `sfg20-emergency-draft:${scopedKey(key)}`,
            JSON.stringify({
                key,
                data,
                serverVersion,
                updatedAt: Date.now(),
                editorType,
                saveContext,
                conflictDetected: false,
            })
        )
    } catch (e) {
        // localStorage may be full or unavailable
    }
}

/**
 * Migrate any emergency drafts from localStorage into localforage.
 * Call on mount to recover from browser crashes.
 */
export async function migrateEmergencyDrafts() {
    try {
        const prefix = "sfg20-emergency-draft:"
        const keys = []
        for (let i = 0; i < localStorage.length; i++) {
            const k = localStorage.key(i)
            if (k?.startsWith(prefix)) keys.push(k)
        }
        const userPrefix = currentUserPrefix()
        for (const storageKey of keys) {
            try {
                // The localStorage key is "sfg20-emergency-draft:{email}:{draftKey}".
                // Extract the original scoped key (everything after the prefix) to
                // preserve the user scope — don't re-scope with the current user.
                const sk = storageKey.slice(prefix.length)
                if (!sk.startsWith(userPrefix)) {
                    continue
                }
                const raw = localStorage.getItem(storageKey)
                if (raw) {
                    const draft = JSON.parse(raw)
                    const existing = await store.getItem(sk)
                    if (!existing || existing.updatedAt < draft.updatedAt) {
                        await store.setItem(sk, draft)
                    }
                }
                localStorage.removeItem(storageKey)
            } catch (e) {
                console.warn("Failed to migrate emergency draft, removing corrupt entry:", storageKey, e)
                try {
                    localStorage.removeItem(storageKey)
                } catch (_) {
                    /* ignore */
                }
            }
        }
    } catch (e) {
        console.warn("Failed to migrate emergency drafts:", e)
    }
}
